import { AuthMethods } from '@/models/auth-methods';
import { AuthUtils } from '@/utils/auth.utils';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import navigationService from './NavigationService';

/**
 * HttpService class provides methods to perform HTTP requests using Axios.
 * It supports GET, POST, PUT, PATCH, and DELETE methods.
 * The base URL for the requests is configured using the VUE_APP_API_URL environment variable.
 *
 * @class HttpService
 * @private {string} apiUrl - The base URL for the API.
 * @private {AxiosInstance} http - The Axios instance used for making HTTP requests.
 *
 * @constructor
 * Initializes the Axios instance with the base URL and default headers.
 * Sets up an interceptor to handle 401 Unauthorized responses by navigating to the login page.
 */
class HttpService {
  private apiUrl: string = process.env.VUE_APP_API_URL || '';
  private http: AxiosInstance;

  constructor() {
    const isSpanishAuth = process.env.VUE_APP_AUTH_METHOD === AuthMethods.Spain;
    this.http = axios.create({
      baseURL: this.apiUrl,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: isSpanishAuth
    });

    this.http.interceptors.request.use(
      (config) => {
        console.log('Request:', config);
        if (config.data) {
          console.log('Request Body:', config.data);
        }
        if (config.headers) {
          console.log('Request Headers:', config.headers);
        }
        return config;
      },
      (error) => {
        console.error('HTTP error:', error);
        return Promise.reject(error);
      }
    );

    this.http.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        console.error('HTTP error:', error);

        if (error.response.status === 401) {
          console.log('Unauthorized error, redirecting to login...');
          navigationService.navigateTo({
            name: AuthUtils.getLoginRouteName(
              process.env.VUE_APP_AUTH_METHOD as AuthMethods
            )
          });
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Sets the Authorization header for the Axios instance.
   * @param {string} token - The token to set in the Authorization header.
   */
  public setAuthorizationHeader(token: string): void {
    this.http.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  /**
   * Sends a GET request to the specified URL.
   * @param {string} url - The URL to send the GET request to.
   * @param {AxiosRequestConfig} [config] - Optional Axios request configuration.
   * @param {Record<string, unknown>} [optionalParameters] - Optional query parameters to include in the URL.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public async get<T>(
    url: string,
    config?: AxiosRequestConfig,
    optionalParameters?: Record<string, unknown>
  ): Promise<T> {
    const fullUrl = this.buildUrlWithParams(url, optionalParameters);
    return this.http.get<T>(fullUrl, config).then((res) => res.data);
  }

  /**
   * Sends a POST request to the specified URL.
   * @param {string} url - The URL to send the POST request to.
   * @param {unknown} [data] - The data to send in the POST request.
   * @param {AxiosRequestConfig} [config] - Optional Axios request configuration.
   * @param {Record<string, unknown>} [optionalParameters] - Optional query parameters to include in the URL.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    optionalParameters?: Record<string, unknown>
  ): Promise<T> {
    const fullUrl = this.buildUrlWithParams(url, optionalParameters);
    return this.http.post<T>(fullUrl, data, config).then((res) => res.data);
  }

  /**
   * Sends a PUT request to the specified URL.
   * @param {string} url - The URL to send the PUT request to.
   * @param {unknown} [data] - The data to send in the PUT request.
   * @param {AxiosRequestConfig} [config] - Optional Axios request configuration.
   * @param {Record<string, unknown>} [optionalParameters] - Optional query parameters to include in the URL.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    optionalParameters?: Record<string, unknown>
  ): Promise<T> {
    const fullUrl = this.buildUrlWithParams(url, optionalParameters);
    return this.http.put<T>(fullUrl, data, config).then((res) => res.data);
  }

  /**
   * Sends a PATCH request to the specified URL.
   * @param {string} url - The URL to send the PATCH request to.
   * @param {unknown} [data] - The data to send in the PATCH request.
   * @param {AxiosRequestConfig} [config] - Optional Axios request configuration.
   * @param {Record<string, unknown>} [optionalParameters] - Optional query parameters to include in the URL.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    optionalParameters?: Record<string, unknown>
  ): Promise<T> {
    const fullUrl = this.buildUrlWithParams(url, optionalParameters);
    return this.http.patch<T>(fullUrl, data, config).then((res) => res.data);
  }

  /**
   * Sends a DELETE request to the specified URL.
   * @param {string} url - The URL to send the DELETE request to.
   * @param {AxiosRequestConfig} [config] - Optional Axios request configuration.
   * @param {Record<string, unknown>} [optionalParameters] - Optional query parameters to include in the URL.
   * @returns {Promise<T>} - A promise that resolves to the response data.
   */
  public async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
    optionalParameters?: Record<string, unknown>
  ): Promise<T> {
    const fullUrl = this.buildUrlWithParams(url, optionalParameters);
    return this.http.delete<T>(fullUrl, config).then((res) => res.data);
  }

  /**
   * Método genérico para peticiones streaming con cualquier verbo HTTP.
   * @param method - Verbo HTTP (GET, POST, etc.)
   * @param url - URL relativa
   * @param data - Datos a enviar (opcional)
   * @param onChunk - Callback para cada chunk recibido
   * @returns Promise<T> - Respuesta completa tipada
   */
  public async streamingRequest<T = string>(
    method: string,
    url: string,
    data?: unknown,
    onChunk?: (chunk: T) => void
  ): Promise<T> {
    const fullUrl = `${this.apiUrl}${url}`;
    const response = await fetch(fullUrl, {
      method,
      headers: {
        'Content-Type': 'application/json'
      },
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Error en la petición streaming');
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let result = '';
    if (reader) {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        result += chunk;
        chunk.split('\n').forEach((line) => {
          if (line.trim()) {
            try {
              const obj = JSON.parse(line);
              if (onChunk) onChunk(obj as T);
            } catch (e) {
              if (onChunk) onChunk(line as unknown as T);
            }
          }
        });
      }
    }
    return result as unknown as T;
  }

  /**
   * POST streaming tipado usando streamingRequest
   */
  public postStreaming<T = string>(
    url: string,
    data?: unknown,
    onChunk?: (chunk: T) => void
  ): Promise<T> {
    return this.streamingRequest<T>('POST', url, data, onChunk);
  }

  /**
   * GET streaming tipado usando streamingRequest
   */
  public getStreaming<T = string>(
    url: string,
    onChunk?: (chunk: T) => void
  ): Promise<T> {
    return this.streamingRequest<T>('GET', url, undefined, onChunk);
  }

  /**
   * PUT streaming tipado usando streamingRequest
   */
  public putStreaming<T = string>(
    url: string,
    data?: unknown,
    onChunk?: (chunk: T) => void
  ): Promise<T> {
    return this.streamingRequest<T>('PUT', url, data, onChunk);
  }

  /**
   * DELETE streaming tipado usando streamingRequest
   */
  public deleteStreaming<T = string>(
    url: string,
    data?: unknown,
    onChunk?: (chunk: T) => void
  ): Promise<T> {
    return this.streamingRequest<T>('DELETE', url, data, onChunk);
  }

  /**
   * Builds a URL with optional query parameters.
   * @param {string} url - The base URL.
   * @param {Record<string, unknown>} [optionalParameters] - Optional query parameters to include in the URL.
   * @returns {string} - The URL with the query parameters appended.
   */
  private buildUrlWithParams(
    url: string,
    optionalParameters?: Record<string, unknown>
  ): string {
    if (!optionalParameters) {
      return url;
    }

    const queryString = new URLSearchParams(
      Object.entries(optionalParameters).reduce(
        (acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        },
        {} as Record<string, string>
      )
    ).toString();

    return queryString
      ? `${url}${url.includes('?') ? '&' : '?'}${queryString}`
      : url;
  }
}

const httpService = new HttpService();
export type HttpResponse<T = unknown> = AxiosResponse<T>;
export default httpService;
